import {
  Context,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDisclosure } from '@equip.health/ui';
import { notesApi, useCreateNoteMutation } from '~/store/services/notes/notes';
import { getNoteTypeBasedOnAppointmentType } from '~/lib/util/notes/notes.util';
import { AppointmentTypeName } from '~/lib/constants/scheduling.constants';
import { deriveNewNoteCommunicationMethod } from '~/lib/util/deriveNewNoteCommunicationMethod';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useLocation } from 'react-router-dom';
import { Path } from '~/routes/constants';
import { useDispatch } from 'react-redux';
import { CacheTag } from '~/store/services/CacheTag';

export type EditNoteDrawerStatus = 'open' | 'closed' | 'minimized';

export type EditNoteDrawerContextValue = {
  appointmentDetails?: AppointmentDetailResponse;
  closeNoteDrawer: () => void;
  drawerStatus: EditNoteDrawerStatus;
  noteId: string | null;
  openNoteDrawer: (
    patientId: string,
    noteId: string | null,
    appointmentDetails?: AppointmentDetailResponse,
  ) => void;
  minimize: () => void;
  unminimize: () => void;
  patientId: string | null;
  isCreateNoteLoading: boolean;
  createNoteError: FetchBaseQueryError | SerializedError | null;
};

export const EMPTY_EDIT_NOTE_DRAWER_CONTEXT: EditNoteDrawerContextValue = {
  closeNoteDrawer: () => {},
  drawerStatus: 'closed',
  noteId: null,
  openNoteDrawer: () => {},
  minimize: () => {},
  unminimize: () => {},
  patientId: null,
  isCreateNoteLoading: false,
  createNoteError: null,
};

export const EditNoteDrawerContext: Context<EditNoteDrawerContextValue> =
  createContext<EditNoteDrawerContextValue>(EMPTY_EDIT_NOTE_DRAWER_CONTEXT);

// This context is solely responsible for opening and closing the note drawer. It should remain slim,
// since as a top level context, its re-renders are costly.
export const EditNoteDrawerContextProvider = ({
  children,
}: PropsWithChildren) => {
  const { currentUser } = useGlobalContext();
  const { onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const dispatch = useDispatch();

  const [drawerStatus, setDrawerStatus] =
    useState<EditNoteDrawerStatus>('closed');
  const [noteId, setNoteId] = useState(null);
  const [patientId, setPatientId] = useState(null);
  // TODO the goal long term is to remove this in favor of appt id and the top level form component is responsible
  // for querying the details, but we must limit changes in this PR due to scope.
  const [appointmentDetails, setAppointmentDetails] = useState<
    AppointmentDetailResponse | undefined
  >();

  const [
    createNote,
    { isLoading: isCreateNoteLoading, error: createNoteError },
  ] = useCreateNoteMutation();

  const openNoteDrawer = useCallback(
    (
      patientId: string,
      noteId: string | null,
      appointmentDetails?: AppointmentDetailResponse,
    ) => {
      setPatientId(patientId);
      setAppointmentDetails(appointmentDetails);
      onOpen();
      setDrawerStatus('open');

      if (noteId) {
        dispatch(
          notesApi.util.invalidateTags([
            { type: CacheTag.GetNote, id: noteId },
          ]),
        );
        setNoteId(noteId);
      } else {
        const noteType = getNoteTypeBasedOnAppointmentType(
          appointmentDetails?.appointmentType as AppointmentTypeName,
        );
        createNote({
          activityDate: appointmentDetails?.appointmentStartDateTime,
          appointmentExternalId: appointmentDetails?.appointmentExternalId,
          authoredByProviderExternalId: currentUser.userId,
          noteCommunicationMethod: deriveNewNoteCommunicationMethod(
            appointmentDetails?.appointmentType,
            appointmentDetails?.appointmentExternalId,
          ),
          noteType,
          patientExternalId: patientId,
        }).then((r) => setNoteId(r.data.noteExternalId));
      }
    },
    [],
  );

  const closeNoteDrawerAndResetState = () => {
    onClose();
    setDrawerStatus('closed');
    setNoteId(null);
    setPatientId(null);
    setAppointmentDetails(undefined);
  };

  useEffect(() => {
    const onChartForPatientOfNote =
      location.pathname.includes(Path.MAUD_PATIENTS) &&
      location.pathname.includes(patientId);

    if (drawerStatus !== 'closed' && !onChartForPatientOfNote) {
      closeNoteDrawerAndResetState();
    }
  }, [location.pathname, patientId, drawerStatus]);

  const minimize = () => {
    setDrawerStatus('minimized');
  };
  const unminimize = () => {
    onOpen();
    setDrawerStatus('open');
  };

  return (
    <EditNoteDrawerContext.Provider
      value={{
        patientId,
        drawerStatus,
        openNoteDrawer,
        closeNoteDrawer: closeNoteDrawerAndResetState,
        noteId,
        appointmentDetails,
        minimize,
        unminimize,
        isCreateNoteLoading,
        createNoteError,
      }}
    >
      {children}
    </EditNoteDrawerContext.Provider>
  );
};

export const useEditNoteDrawerContext = (): EditNoteDrawerContextValue => {
  const context = useContext(EditNoteDrawerContext);
  if (!context) {
    throw new Error(
      'useEditNoteDrawerContext must be used within a EditNoteDrawerContextProvider.',
    );
  }
  return context;
};
